import {
	IconAlertTriangle,
	IconCheck,
	IconClock,
	IconLoader,
	IconPlayerPause,
	IconRefresh,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { glanceDateFormatOptions } from "src/common/utils/date-utils/glanceDateFormat.ts";
import { findTreatedIssue } from "src/features/integrations/issues/issueTreatments.ts";
import type { FusionHubDataSourceWithIntegration } from "src/features/integrations/overview/table/IntegrationsTableContent.tsx";
import { useLocale } from "src/locales/useLocale.ts";
import {
	resolveSyncStatusKind,
	type SyncStatusInput,
	toSyncStatusInput,
} from "./resolveSyncStatusKind.ts";
import type { SyncStatusPresentation } from "./syncStatusPresentation.ts";

export type { SyncStatusInput } from "./resolveSyncStatusKind.ts";

export const useDataSourceStatus = (
	dataSource: FusionHubDataSourceWithIntegration,
): SyncStatusPresentation | null =>
	useSyncStatus(toSyncStatusInput(dataSource));

export const useSyncStatus = (
	input: SyncStatusInput,
): SyncStatusPresentation | null => {
	const { t } = useTranslation();
	const { format, formatRelative, formatElapsed } = useLocale();
	const kind = resolveSyncStatusKind(input);

	const toDate = (value: string | null | undefined) =>
		value ? new Date(value) : null;
	const since = (date: Date) => format(date, glanceDateFormatOptions(date));

	switch (kind) {
		case null:
			return null;
		case "paused":
			return {
				tone: "neutral",
				Icon: IconPlayerPause,
				word: t("Paused"),
				phrase: t("Paused"),
				timestamp: null,
			};
		case "attention": {
			const detectedAt = toDate(
				findTreatedIssue(input.issues)?.issue.detectedAt,
			);
			return {
				tone: "attention",
				Icon: IconAlertTriangle,
				word: t("Needs attention"),
				phrase: detectedAt
					? t("Needs attention since {{date}}", { date: since(detectedAt) })
					: t("Needs attention"),
				timestamp: detectedAt,
			};
		}
		case "queued": {
			const queuedAt = toDate(input.currentJobQueuedAt);
			return {
				tone: "syncing",
				Icon: IconClock,
				word: t("Queued"),
				phrase: queuedAt
					? t("Queued since {{date}}", { date: since(queuedAt) })
					: t("Queued"),
				timestamp: queuedAt,
			};
		}
		case "syncing": {
			const startedAt = toDate(input.currentJobStartedAt);
			return {
				tone: "syncing",
				Icon: IconRefresh,
				spin: "smooth",
				word: t("Syncing"),
				phrase: startedAt
					? t("Syncing for {{elapsed}}", { elapsed: formatElapsed(startedAt) })
					: t("Syncing"),
				timestamp: startedAt,
			};
		}
		case "processing": {
			const lastSuccessfulSyncAt = toDate(input.lastSuccessfulSyncAt);
			return {
				tone: "neutral",
				Icon: IconLoader,
				spin: "stepped",
				word: t("Processing"),
				phrase: lastSuccessfulSyncAt
					? t("Processing for {{elapsed}}", {
							elapsed: formatElapsed(lastSuccessfulSyncAt),
						})
					: t("Processing"),
				timestamp: lastSuccessfulSyncAt,
			};
		}
		case "succeeded": {
			const lastSuccessfulSyncAt = toDate(input.lastSuccessfulSyncAt);
			return {
				tone: "neutral",
				Icon: IconCheck,
				word: t("Succeeded"),
				phrase: lastSuccessfulSyncAt
					? t("Succeeded {{when}}", {
							when: formatRelative(lastSuccessfulSyncAt),
						})
					: t("Succeeded"),
				timestamp: lastSuccessfulSyncAt,
			};
		}
	}
};
